import  { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Paper,
  Stack,
  TextInput,
  Textarea,
  Button,
  Group,
  Grid,
  Image,
  Loader,
  FileInput,
  NumberInput,
  SimpleGrid,
  Card,
  ActionIcon,
  Text,
  Modal,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useDisclosure } from '@mantine/hooks';
import axios from 'axios';
import { useAppSelector } from '../../../../store/hooks';
import { IconTrash, IconEdit, IconUpload } from '@tabler/icons-react';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'https://thalvora-exports.onrender.com';

interface Certification {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  issuer: string;
  issueDate: string;
  expiryDate: string;
  order: number;
  active: boolean;
}

export const CertificationsGalleryPage = () => {
  const { token } = useAppSelector((state) => state.auth);
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Certification | null>(null);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const form = useForm({
    initialValues: {
      title: '',
      description: '',
      issuer: '',
      issueDate: '',
      expiryDate: '',
      order: 0,
      active: true,
    },
  });

  const fetchCertifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/gallery/CERTIFICATIONS`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const gallery = res.data;
      const items = gallery.items
        .filter((item: any) => item.type === 'IMAGE')
        .map((item: any) => {
          let meta = {};
          try {
            meta = JSON.parse(item.metadata || '{}');
          } catch (e) {}
          return {
            id: item.id,
            title: item.title,
            description: item.description,
            imageUrl: item.imageUrl,
            issuer: (meta as any).issuer || '',
            issueDate: (meta as any).issueDate || '',
            expiryDate: (meta as any).expiryDate || '',
            order: (meta as any).order || 0,
            active: item.active,
          };
        })
        .sort((a: Certification, b: Certification) => a.order - b.order);
      setCertifications(items);
    } catch (error) {
      console.error('Failed to fetch certifications:', error);
      notifications.show({ title: 'Error', message: 'Failed to load certifications', color: 'red' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCertifications();
  }, []);

  const resetForm = () => {
    form.reset();
    setImageFile(null);
    setEditingId(null);
  };

  const handleSubmit = async (values: typeof form.values) => {
    if (!imageFile && !editingId) {
      notifications.show({ title: 'Error', message: 'Please select an image', color: 'red' });
      return;
    }

    setSubmitting(true);
    const formData = new FormData();
    const metadata = {
      issuer: values.issuer,
      issueDate: values.issueDate,
      expiryDate: values.expiryDate,
      order: values.order,
    };
    const data = {
      title: values.title,
      description: values.description,
      type: 'IMAGE',
      active: values.active,
      orderIndex: 0,
      metadata: JSON.stringify(metadata),
    };
    const jsonBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
    formData.append('data', jsonBlob);
    if (imageFile) formData.append('file', imageFile);

    try {
      if (editingId) {
        await axios.put(`${API_URL}/gallery/items/${editingId}`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Certification updated', color: 'green' });
      } else {
        await axios.post(`${API_URL}/gallery/CERTIFICATIONS/items`, formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
        notifications.show({ title: 'Success', message: 'Certification added', color: 'green' });
      }
      resetForm();
      fetchCertifications();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Operation failed', color: 'red' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSubmitting(true);
    try {
      await axios.delete(`${API_URL}/gallery/items/${deleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notifications.show({ title: 'Success', message: 'Certification deleted', color: 'green' });
      closeDeleteModal();
      fetchCertifications();
    } catch (error) {
      notifications.show({ title: 'Error', message: 'Delete failed', color: 'red' });
    } finally {
      setSubmitting(false);
      setDeleteTarget(null);
    }
  };

  const editCert = (cert: Certification) => {
    setEditingId(cert.id);
    form.setValues({
      title: cert.title,
      description: cert.description,
      issuer: cert.issuer,
      issueDate: cert.issueDate,
      expiryDate: cert.expiryDate,
      order: cert.order,
      active: true,
    });
    setImageFile(null);
  };

  const cancelEdit = () => {
    resetForm();
  };

  if (loading) {
    return (
      <Container className="flex justify-center items-center h-64">
        <Loader size="lg" />
      </Container>
    );
  }

  return (
    <Container size="xl">
      <Title order={2} mb="lg">Manage Certifications</Title>

      <Grid>
        <Grid.Col span={{ base: 12, md: 5 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">{editingId ? 'Edit Certification' : 'Add New Certification'}</Title>
            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Certification Name *"
                  placeholder="e.g., IEC Certificate"
                  {...form.getInputProps('title')}
                  required
                />
                <Textarea
                  label="Description"
                  placeholder="Brief description"
                  {...form.getInputProps('description')}
                />
                <TextInput
                  label="Issuer"
                  placeholder="e.g., DGFT"
                  {...form.getInputProps('issuer')}
                />
                <TextInput
                  label="Issue Date"
                  type="date"
                  {...form.getInputProps('issueDate')}
                />
                <TextInput
                  label="Expiry Date"
                  type="date"
                  {...form.getInputProps('expiryDate')}
                />
                <NumberInput
                  label="Display Order"
                  placeholder="0, 1, 2..."
                  {...form.getInputProps('order')}
                />
                <FileInput
                  label="Certification Image"
                  placeholder="Click to upload"
                  accept="image/png,image/jpeg,image/svg+xml"
                  value={imageFile}
                  onChange={setImageFile}
                  leftSection={<IconUpload size={16} />}
                />
                <Group justify="flex-end" mt="md">
                  {editingId && (
                    <Button variant="light" onClick={cancelEdit}>Cancel</Button>
                  )}
                  <Button type="submit" color="primary" loading={submitting}>
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 7 }}>
          <Paper withBorder p="xl" radius="md">
            <Title order={4} mb="md">Existing Certifications ({certifications.length})</Title>
            {certifications.length === 0 ? (
              <Text c="dimmed">No certifications added yet.</Text>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {certifications.map((cert) => (
                  <Card key={cert.id} withBorder padding="sm">
                    <Card.Section>
                      <Image
                        src={cert.imageUrl.startsWith('http') ? cert.imageUrl : `${API_URL}${cert.imageUrl}`}
                        height={100}
                        fit="contain"
                        fallbackSrc="https://via.placeholder.com/200x100?text=No+Image"
                      />
                    </Card.Section>
                    <Stack mt="xs" gap={5}>
                      <Text fw={600}>{cert.title}</Text>
                      <Text size="xs" c="dimmed">{cert.issuer}</Text>
                      <Text size="xs">Issued: {cert.issueDate || '—'}</Text>
                      <Text size="xs">Expires: {cert.expiryDate || '—'}</Text>
                      <Group justify="space-between" mt="xs">
                        <ActionIcon color="blue" onClick={() => editCert(cert)}>
                          <IconEdit size={16} />
                        </ActionIcon>
                        <ActionIcon color="red" onClick={() => { setDeleteTarget(cert); openDeleteModal(); }}>
                          <IconTrash size={16} />
                        </ActionIcon>
                      </Group>
                    </Stack>
                  </Card>
                ))}
              </SimpleGrid>
            )}
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpened} onClose={closeDeleteModal} title="Confirm Deletion">
        <Text>Are you sure you want to delete <strong>{deleteTarget?.title}</strong>? This action cannot be undone.</Text>
        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={closeDeleteModal}>Cancel</Button>
          <Button color="red" onClick={handleDelete} loading={submitting}>Delete</Button>
        </Group>
      </Modal>
    </Container>
  );
};